/**
 * Utility functions for dynamic lifecycle type names
 */

/**
 * Get lifecycle type names from localStorage
 */
export const getLifecycleTypeNames = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem("lifecycleTypeNames");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error(
      "Error parsing lifecycle type names from localStorage:",
      error
    );
  }

  return {
    DEAL: "Deal",
    LEAD: "Lead",
    PIPELINE: "Pipeline",
    PROPOSAL: "Proposal",
    CONTRACT: "Contract",
  };
};

/**
 * Get dynamic name for a specific lifecycle type
 */
export const getLifecycleTypeName = (lifecycleTypeKey: string): string => {
  const names = getLifecycleTypeNames();
  return names[lifecycleTypeKey] || lifecycleTypeKey;
};

/**
 * Get dynamic plural name for a specific lifecycle type
 */
export const pluralizeLifecycleName = (singularName: string): string => {
  if (singularName.toLowerCase().endsWith("s")) {
    return singularName;
  }
  if (singularName.endsWith("y")) {
    return singularName.slice(0, -1) + "ies";
  }
  return singularName + "s";
};

export const getLifecycleTypePluralName = (
  lifecycleTypeKey: string
): string => pluralizeLifecycleName(getLifecycleTypeName(lifecycleTypeKey));

const LIFECYCLE_TYPE_KEYS = ["LEAD", "PIPELINE", "DEAL", "PROPOSAL", "CONTRACT"];

export const getLifecycleEntityLabel = (
  entityName: string,
  names?: Record<string, string>
): string => {
  const key = entityName.trim().toUpperCase();
  if (!LIFECYCLE_TYPE_KEYS.includes(key)) return entityName;
  return names?.[key] || getLifecycleTypeName(key);
};

export interface LifecycleLabels {
  lead: string;
  leads: string;
  pipeline: string;
  pipelines: string;
  deal: string;
  deals: string;
  proposal: string;
  proposals: string;
  contract: string;
  contracts: string;
}

export const getLifecycleLabels = (names?: Record<string, string>): LifecycleLabels => {
  const singular = (key: string) => names?.[key] || getLifecycleTypeName(key);
  return {
    lead: singular("LEAD"),
    leads: pluralizeLifecycleName(singular("LEAD")),
    pipeline: singular("PIPELINE"),
    pipelines: pluralizeLifecycleName(singular("PIPELINE")),
    deal: singular("DEAL"),
    deals: pluralizeLifecycleName(singular("DEAL")),
    proposal: singular("PROPOSAL"),
    proposals: pluralizeLifecycleName(singular("PROPOSAL")),
    contract: singular("CONTRACT"),
    contracts: pluralizeLifecycleName(singular("CONTRACT")),
  };
};
